'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cn } from '@ncai-axe/react-ui-v3';
import styles from './styles.module.scss';
import { usePopover } from './PopoverContext';
import { PopoverContentCoreProps } from './types';

const DEFAULT_PLACEMENT = 'bottom span-right' as const;

type CSSWithAnchor = React.CSSProperties & {
  ['positionAnchor']?: string;
  ['positionArea']?: string;
};

interface PopoverContentImplProps extends PopoverContentCoreProps {
  id: string;
  triggerId: string;
  children: React.ReactNode;
  className?: string;

  onClick?: (ev: React.MouseEvent<HTMLDivElement>) => void;
  onBeforeToggle?: (ev: React.ToggleEvent) => void;
  onToggle?: (ev: React.ToggleEvent) => void;
}

function PopoverContentImpl({
  id,
  children,
  triggerId,
  autoPlacement = false,
  placement = DEFAULT_PLACEMENT,
  hideOnOutsideClick = true,
  hideOnContentClick = true,
  className,
  onClick,
  onBeforeToggle,
  onToggle,
}: PopoverContentImplProps) {
  const [currentPlacement, setCurrentPlacement] = useState<string>(placement);
  useEffect(() => setCurrentPlacement(placement), [placement]);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const popoverValue = hideOnOutsideClick ? 'auto' : 'manual';

  const contentRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      contentRef.current = node;
      if (node) {
        node.setAttribute('popover', popoverValue);
      }
    },
    [popoverValue],
  );

  const calculateOptimalPlacement = useCallback(() => {
    if (!autoPlacement) return;
    const triggerElement = triggerId
      ? document.getElementById(triggerId)
      : (document.querySelector(
          `[popovertarget="${id}"]`,
        ) as HTMLElement | null);
    if (!triggerElement) return;
    const { top, height } = triggerElement.getBoundingClientRect();
    const next =
      top + height - 200 > window.innerHeight / 2
        ? `top ${placement}`
        : `bottom ${placement}`;
    setCurrentPlacement(next);
  }, [autoPlacement, id, placement, triggerId]);

  const handleBeforeToggle = useCallback(
    (ev: React.ToggleEvent) => {
      onBeforeToggle?.(ev);
      if (ev.newState === 'open') {
        calculateOptimalPlacement();
      }
    },
    [calculateOptimalPlacement, onBeforeToggle],
  );

  const handleToggle = useCallback(
    (ev: React.ToggleEvent) => {
      onToggle?.(ev);
    },
    [onToggle],
  );

  const handleClickContent = useCallback(
    (ev: React.MouseEvent<HTMLDivElement>) => {
      onClick?.(ev);
      if (hideOnContentClick) {
        contentRef.current?.hidePopover?.();
      }
    },
    [hideOnContentClick, onClick],
  );

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.setAttribute('popover', popoverValue);
    }
  }, [popoverValue]);

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    const toggleHandler = (ev: Event) => {
      handleToggle(ev as unknown as React.ToggleEvent);
    };
    const beforeToggleHandler = (ev: Event) => {
      handleBeforeToggle(ev as unknown as React.ToggleEvent);
    };

    element.addEventListener('toggle', toggleHandler);
    element.addEventListener('beforetoggle', beforeToggleHandler);

    return () => {
      element.removeEventListener('toggle', toggleHandler);
      element.removeEventListener('beforetoggle', beforeToggleHandler);
    };
  }, [handleToggle, handleBeforeToggle]);

  const contentStyle: CSSWithAnchor = useMemo(
    () => ({
      ['positionArea']: currentPlacement,
      ['positionAnchor']: `--${id}`,
    }),
    [currentPlacement, id],
  );

  return (
    <div
      id={id}
      ref={contentRefCallback}
      className={cn(styles.popoverContent, className)}
      style={contentStyle}
      onClick={handleClickContent}
    >
      {children}
    </div>
  );
}

PopoverContentImpl.displayName = 'PopoverContent';

export default function PopoverContent({
  children,
  className,
  onToggle,
}: {
  children: React.ReactNode;
  className?: string;
  onToggle?: (ev: React.ToggleEvent) => void;
}) {
  const { triggerId, contentId, ...props } = usePopover();
  return (
    <PopoverContentImpl
      className={className}
      id={contentId}
      triggerId={triggerId}
      onToggle={onToggle}
      {...props}
    >
      {children}
    </PopoverContentImpl>
  );
}

PopoverContent.displayName = 'PopoverContent';
